import * as React from "react";
import { createPortal } from "react-dom";
import { cva, type VariantProps } from "class-variance-authority";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Calendar from "react-calendar";
// @ts-ignore - CSS import doesn't have type definitions
import "react-calendar/dist/Calendar.css";
import { cn } from "../../lib/utils";
import { Button } from "../Button";
import { Icon } from "../../lib/icons";
import { ListItem } from "../ListItem";

export type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
};

export type PresetOption = {
  label: string;
  value: string;
  getDateRange: () => DateRange;
};

const dateRangePickerVariants = cva(
  "relative flex items-center gap-2 border rounded-large transition-all font-functional font-size-100 leading-100 bg-surface-fill-neutral-intense",
  {
    variants: {
      size: {
        small: "h-[28px] px-3 text-xs gap-2",
        medium: "h-[36px] px-4 text-sm gap-2",
        large: "h-[44px] px-5 text-base gap-3",
      },
      isDisabled: {
        true: `
        border-[var(--border-width-thinner)]
        hover:border-action-outline-neutral-disabled
        border-action-outline-neutral-disabled
        bg-surface-fill-neutral-intense cursor-not-allowed opacity-60`,
        false: `
        border-action-outline-neutral-faded 
        hover:border-action-outline-primary-hover 
        focus-within:border-action-outline-primary-hover 
        focus-within:ring-2 
        ring-action-outline-primary-faded-hover`,
      },
    },
    defaultVariants: {
      size: "medium",
      isDisabled: false,
    },
  }
);

// Default preset options
const defaultPresets: PresetOption[] = [
  {
    label: "Today",
    value: "today",
    getDateRange: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return { startDate: today, endDate: today };
    },
  },
  {
    label: "Yesterday",
    value: "yesterday",
    getDateRange: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      return { startDate: yesterday, endDate: yesterday };
    },
  },
  {
    label: "Last 7 days",
    value: "last7days",
    getDateRange: () => {
      const endDate = new Date();
      endDate.setHours(0, 0, 0, 0);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    },
  },
  {
    label: "Last 30 days",
    value: "last30days",
    getDateRange: () => {
      const endDate = new Date();
      endDate.setHours(0, 0, 0, 0);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 29);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    },
  },
  {
    label: "Last 6 months",
    value: "last6months",
    getDateRange: () => {
      const endDate = new Date();
      endDate.setHours(0, 0, 0, 0);
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    },
  },
  {
    label: "All time",
    value: "alltime",
    getDateRange: () => {
      return { startDate: null, endDate: null };
    },
  },
];

export interface DateRangePickerProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "onChange" | "defaultValue"
  > {
  /**
   * Selected date range (controlled)
   */
  value?: DateRange;
  /**
   * Default date range (uncontrolled)
   */
  defaultValue?: DateRange;
  /**
   * Callback when date range changes
   */
  onChange?: (dateRange: DateRange) => void;
  /**
   * Placeholder text when no date range is selected
   */
  placeholder?: string;
  /**
   * Whether the date range picker is disabled
   */
  isDisabled?: boolean;
  /**
   * Size of the date range picker
   */
  size?: "small" | "medium" | "large";
  /**
   * Custom class for the trigger button
   */
  triggerClassName?: string;
  /**
   * Custom class for the calendar popup
   */
  calendarClassName?: string;
  /**
   * Minimum selectable date
   */
  minDate?: Date | string;
  /**
   * Maximum selectable date
   */
  maxDate?: Date | string;
  /**
   * Date format function
   */
  formatDate?: (date: Date) => string;
  /**
   * Preset options to show in the sidebar
   */
  presets?: PresetOption[];
  /**
   * Whether to show preset options
   */
  showPresets?: boolean;
  /**
   * Label for the trigger button when a preset is selected
   */
  selectedPresetLabel?: string;
}

// Helper functions
const parseDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === "string") {
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatDateDefault = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const formatMonthYear = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

export const DateRangePicker = React.forwardRef<
  HTMLDivElement,
  DateRangePickerProps
>(
  (
    {
      className,
      value: controlledValue,
      defaultValue,
      onChange,
      placeholder = "Select date range",
      isDisabled = false,
      size = "medium",
      triggerClassName,
      calendarClassName,
      minDate,
      maxDate,
      formatDate = formatDateDefault,
      presets = defaultPresets,
      showPresets = true,
      selectedPresetLabel,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = React.useState<DateRange>(
      defaultValue || { startDate: null, endDate: null }
    );
    const [tempValue, setTempValue] = React.useState<DateRange>(
      defaultValue || { startDate: null, endDate: null }
    );
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedPreset, setSelectedPreset] = React.useState<string | null>(
      null
    );
    const [startMonth, setStartMonth] = React.useState(new Date());
    const [endMonth, setEndMonth] = React.useState(() => {
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      return nextMonth;
    });

    const datePickerRef = React.useRef<HTMLDivElement>(null);
    const calendarRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState({
      top: 0,
      left: 0,
      width: 0,
      bottom: 0,
    });
    const [dropdownPlacement, setDropdownPlacement] = React.useState<
      "top" | "bottom"
    >("bottom");
    const [isInsideModal, setIsInsideModal] = React.useState(false);

    const value =
      controlledValue !== undefined ? controlledValue : uncontrolledValue;

    const hasValue = value.startDate !== null || value.endDate !== null;

    const handleOpenChange = (newOpen: boolean) => {
      if (!isDisabled) {
        setIsOpen(newOpen);
        if (newOpen) {
          // Set temp value to current value when opening
          setTempValue(value);
        }
      }
    };

    const toggleOpen = () => {
      handleOpenChange(!isOpen);
    };

    const handlePresetClick = (preset: PresetOption) => {
      const dateRange = preset.getDateRange();
      setTempValue(dateRange);
      setSelectedPreset(preset.value);
    };

    const handleStartDateChange = (
      value: Date | null | [Date | null, Date | null],
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      const selectedDate = Array.isArray(value) ? value[0] : value;
      if (!selectedDate) return;

      const newStartDate = new Date(selectedDate);
      newStartDate.setHours(0, 0, 0, 0);

      setTempValue((prev) => ({
        startDate: newStartDate,
        endDate: prev.endDate,
      }));
      setSelectedPreset(null);
    };

    const handleEndDateChange = (
      value: Date | null | [Date | null, Date | null],
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      const selectedDate = Array.isArray(value) ? value[0] : value;
      if (!selectedDate) return;

      const newEndDate = new Date(selectedDate);
      newEndDate.setHours(0, 0, 0, 0);

      setTempValue((prev) => ({
        startDate: prev.startDate,
        endDate: newEndDate,
      }));
      setSelectedPreset(null);
    };

    const handleApply = () => {
      if (controlledValue === undefined) {
        setUncontrolledValue(tempValue);
      }
      onChange?.(tempValue);
      setIsOpen(false);
    };

    const handleCancel = () => {
      setTempValue(value);
      setIsOpen(false);
    };

    const handlePrevStartMonth = () => {
      const newMonth = new Date(startMonth);
      newMonth.setMonth(newMonth.getMonth() - 1);
      setStartMonth(newMonth);
    };

    const handleNextStartMonth = () => {
      const newMonth = new Date(startMonth);
      newMonth.setMonth(newMonth.getMonth() + 1);
      setStartMonth(newMonth);
    };

    const handlePrevEndMonth = () => {
      const newMonth = new Date(endMonth);
      newMonth.setMonth(newMonth.getMonth() - 1);
      setEndMonth(newMonth);
    };

    const handleNextEndMonth = () => {
      const newMonth = new Date(endMonth);
      newMonth.setMonth(newMonth.getMonth() + 1);
      setEndMonth(newMonth);
    };

    // Check if date picker is inside a modal
    React.useEffect(() => {
      if (isOpen && datePickerRef.current) {
        let element: HTMLElement | null = datePickerRef.current;
        let foundModal = false;
        while (element && !foundModal) {
          const styles = window.getComputedStyle(element);
          const zIndex = parseInt(styles.zIndex, 10);
          if (zIndex === 10000 || element.getAttribute("role") === "dialog") {
            foundModal = true;
            setIsInsideModal(true);
            break;
          }
          element = element.parentElement;
        }
        if (!foundModal) {
          setIsInsideModal(false);
        }
      }
    }, [isOpen]);

    // Update position and placement when calendar opens or window resizes
    React.useEffect(() => {
      if (isOpen && datePickerRef.current) {
        const updatePosition = () => {
          const rect = datePickerRef.current?.getBoundingClientRect();
          const calendarHeight = calendarRef.current?.offsetHeight || 600; // Estimated height

          if (rect) {
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const gap = 4;

            // Calculate available space
            const spaceBelow = viewportHeight - rect.bottom - gap;
            const spaceAbove = rect.top - gap;

            // Determine vertical placement
            const placement =
              spaceBelow >= calendarHeight || spaceBelow >= spaceAbove
                ? "bottom"
                : "top";
            setDropdownPlacement(placement);

            // Calculate horizontal position
            let leftPosition = rect.left;
            const calendarWidth = calendarRef.current?.offsetWidth || 900; // Estimated width

            // Adjust if dropdown would overflow right edge
            if (leftPosition + calendarWidth > viewportWidth) {
              leftPosition = viewportWidth - calendarWidth - 10; // 10px margin from edge
            }

            // Ensure it doesn't overflow left edge
            if (leftPosition < 10) {
              leftPosition = 10;
            }

            setPosition({
              top: rect.top,
              left: leftPosition,
              width: rect.width,
              bottom: rect.bottom,
            });
          }
        };

        updatePosition();

        // Small delay to ensure calendar is rendered for accurate measurements
        const timeoutId = setTimeout(updatePosition, 10);

        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
          clearTimeout(timeoutId);
          window.removeEventListener("resize", updatePosition);
          window.removeEventListener("scroll", updatePosition, true);
        };
      }
    }, [isOpen]);

    // Close calendar when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          datePickerRef.current &&
          !datePickerRef.current.contains(event.target as Node) &&
          calendarRef.current &&
          !calendarRef.current.contains(event.target as Node)
        ) {
          handleCancel();
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen, value]);

    // Close on escape key
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleCancel();
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        return () => {
          document.removeEventListener("keydown", handleEscape);
        };
      }
    }, [isOpen, value]);

    const minDateParsed = parseDate(minDate);
    const maxDateParsed = parseDate(maxDate);

    const getDisplayText = () => {
      if (selectedPresetLabel) {
        return selectedPresetLabel;
      }

      if (selectedPreset) {
        const preset = presets.find((p) => p.value === selectedPreset);
        if (preset) return preset.label;
      }

      if (!hasValue) {
        return placeholder;
      }

      if (value.startDate && value.endDate) {
        if (isSameDay(value.startDate, value.endDate)) {
          return formatDate(value.startDate);
        }
        return `${formatDate(value.startDate)} - ${formatDate(value.endDate)}`;
      }

      if (value.startDate) {
        return `From ${formatDate(value.startDate)}`;
      }

      if (value.endDate) {
        return `Until ${formatDate(value.endDate)}`;
      }

      return placeholder;
    };

    return (
      <div ref={datePickerRef} className={cn("relative w-fit", className)}>
        <div
          className={cn(
            dateRangePickerVariants({
              size,
              isDisabled,
            }),
            "cursor-pointer",
            triggerClassName
          )}
          onClick={!isDisabled ? toggleOpen : undefined}
          role="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-disabled={isDisabled}
          {...props}
        >
          <CalendarIcon
            className={cn(
              "shrink-0 w-4 h-4",
              isDisabled
                ? "text-surface-ink-neutral-disabled"
                : "text-surface-ink-neutral-muted"
            )}
          />

          <span
            className={cn(
              "flex-1 text-left truncate",
              !hasValue && "text-surface-ink-neutral-muted",
              isDisabled && "text-surface-ink-neutral-disabled"
            )}
          >
            {getDisplayText()}
          </span>

          <Icon
            name="chevronDown"
            size={16}
            className={cn(
              "shrink-0 transition-transform",
              isDisabled
                ? "text-surface-ink-neutral-disabled"
                : "text-surface-ink-neutral-muted",
              isOpen && "rotate-180"
            )}
          />
        </div>

        {/* Calendar Popup Portal */}
        {typeof document !== "undefined" &&
          isOpen &&
          !isDisabled &&
          (() => {
            const gap = 4;
            const calendarHeight = calendarRef.current?.offsetHeight || 0;

            // Calculate top position based on placement
            const calendarTop =
              dropdownPlacement === "bottom"
                ? position.bottom + gap
                : position.top - calendarHeight - gap;

            const calendarPopup = (
              <div
                ref={calendarRef}
                style={{
                  position: "fixed",
                  top: `${calendarTop}px`,
                  left: `${position.left}px`,
                  zIndex: isInsideModal ? 10001 : 9999,
                  maxHeight:
                    dropdownPlacement === "bottom"
                      ? `${window.innerHeight - position.bottom - gap - 10}px`
                      : `${position.top - gap - 10}px`,
                  overflowY: "auto",
                }}
                className={cn(
                  "bg-surface-fill-neutral-intense rounded-large shadow-lg border border-surface-outline-neutral-subtle",
                  calendarClassName
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex">
                  {/* Presets Sidebar */}
                  {showPresets && presets.length > 0 && (
                    <div className="w-48 border-r border-surface-outline-neutral-subtle p-4 flex flex-col">
                      {presets.map((preset) => (
                        <ListItem
                          key={preset.value}
                          title={preset.label}
                          type="single"
                          showChevron={false}
                          isSelected={selectedPreset === preset.value}
                          onClick={() => handlePresetClick(preset)}
                          containerClassName="mb-1 last:mb-0"
                        />
                      ))}
                    </div>
                  )}

                  {/* Calendars Section */}
                  <div className="p-4">
                    <div className="flex gap-6">
                      {/* Start Date Calendar */}
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col px-3">
                          <p className="text-body-small-medium">Start Date</p>
                        </div>
                        <div className="flex flex-col p-5 rounded-[8px] bg-surface-fill-neutral-moderate">
                          <div className="flex items-center justify-between mb-3 px-3">
                            <h3 className="text-body-small-medium font-medium text-surface-ink-neutral-normal">
                              {formatMonthYear(startMonth)}
                            </h3>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handlePrevStartMonth}
                                className="p-1 hover:bg-surface-fill-neutral-faded rounded transition-colors"
                                aria-label="Previous month"
                              >
                                <ChevronLeft className="w-4 h-4 text-surface-ink-neutral-normal" />
                              </button>
                              <button
                                onClick={handleNextStartMonth}
                                className="p-1 hover:bg-surface-fill-neutral-faded rounded transition-colors"
                                aria-label="Next month"
                              >
                                <ChevronRight className="w-4 h-4 text-surface-ink-neutral-normal" />
                              </button>
                            </div>
                          </div>
                          <div className="date-range-calendar-wrapper">
                            <Calendar
                              onChange={handleStartDateChange}
                              value={tempValue.startDate}
                              minDate={minDateParsed ?? undefined}
                              maxDate={
                                tempValue.endDate
                                  ? tempValue.endDate
                                  : maxDateParsed ?? undefined
                              }
                              activeStartDate={startMonth}
                              onActiveStartDateChange={({
                                activeStartDate,
                              }) => {
                                if (activeStartDate)
                                  setStartMonth(activeStartDate);
                              }}
                              locale="en-US"
                              showNavigation={false}
                              showNeighboringMonth={true}
                              showFixedNumberOfWeeks={true}
                              formatShortWeekday={(locale, date) => {
                                const weekdayNames = [
                                  "SUN",
                                  "MON",
                                  "TUE",
                                  "WED",
                                  "THU",
                                  "FRI",
                                  "SAT",
                                ];
                                return weekdayNames[date.getDay()];
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* End Date Calendar */}
                      <div className="flex flex-col gap-3 ">
                        <div className="flex flex-col px-3">
                          <p className="text-body-small-medium">End Date</p>
                        </div>
                        <div className="flex flex-col p-5 rounded-[8px] bg-surface-fill-neutral-moderate">
                          <div className="flex items-center justify-between mb-3 px-3">
                            <h3 className="text-body-small-medium font-medium text-surface-ink-neutral-normal">
                              {formatMonthYear(endMonth)}
                            </h3>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handlePrevEndMonth}
                                className="p-1 hover:bg-surface-fill-neutral-faded rounded transition-colors"
                                aria-label="Previous month"
                              >
                                <ChevronLeft className="w-4 h-4 text-surface-ink-neutral-normal" />
                              </button>
                              <button
                                onClick={handleNextEndMonth}
                                className="p-1 hover:bg-surface-fill-neutral-faded rounded transition-colors"
                                aria-label="Next month"
                              >
                                <ChevronRight className="w-4 h-4 text-surface-ink-neutral-normal" />
                              </button>
                            </div>
                          </div>
                          <div className="date-range-calendar-wrapper">
                            <Calendar
                              onChange={handleEndDateChange}
                              value={tempValue.endDate}
                              minDate={
                                tempValue.startDate
                                  ? tempValue.startDate
                                  : minDateParsed ?? undefined
                              }
                              maxDate={maxDateParsed ?? undefined}
                              activeStartDate={endMonth}
                              onActiveStartDateChange={({
                                activeStartDate,
                              }) => {
                                if (activeStartDate)
                                  setEndMonth(activeStartDate);
                              }}
                              locale="en-US"
                              showNavigation={false}
                              showNeighboringMonth={true}
                              showFixedNumberOfWeeks={true}
                              formatShortWeekday={(locale, date) => {
                                const weekdayNames = [
                                  "SUN",
                                  "MON",
                                  "TUE",
                                  "WED",
                                  "THU",
                                  "FRI",
                                  "SAT",
                                ];
                                return weekdayNames[date.getDay()];
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer with Current Selection and Actions */}
                    <div className="mt-4 pt-4 border-t border-surface-outline-neutral-subtle flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-action-fill-primary-default"></div>
                        <span className="text-sm text-surface-ink-neutral-normal">
                          {tempValue.startDate && tempValue.endDate
                            ? isSameDay(tempValue.startDate, tempValue.endDate)
                              ? "Today"
                              : `${formatDate(
                                  tempValue.startDate
                                )} - ${formatDate(tempValue.endDate)}`
                            : tempValue.startDate
                            ? `From ${formatDate(tempValue.startDate)}`
                            : tempValue.endDate
                            ? `Until ${formatDate(tempValue.endDate)}`
                            : "No selection"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          color="neutral"
                          size="small"
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          color="primary"
                          size="small"
                          onClick={handleApply}
                        >
                          Apply date range
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );

            return createPortal(calendarPopup, document.body);
          })()}
      </div>
    );
  }
);

DateRangePicker.displayName = "DateRangePicker";

export { dateRangePickerVariants };
